import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {
  Menu as MuiMenu,
  Tooltip,
  Box,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { BUTTON_VARIANTS } from '../button/button.constants';
import { Button } from '../button/Button.component';

const StyledMenu = styled(MuiMenu)`
  color: ${({ theme }) => theme.palette.grey[40]};
  &:hover {
    color: ${({ theme }) => theme.palette.primary[5]};
  }
  &.Mui-disabled {
    color: ${({ theme }) => theme.palette.grey[5]};
  }
`;

/**
* Renders a <Menu /> component.
* @param {Object} props
* @param {node} props.text - The text/node shown on the menu root.
* @param {node} props.icon - Has a default icon if not provided. Set to null for no-icon at all.
* @param {node | node[]} props.children - Children for the menu, should be <MenuItem> components
*/
export const Menu = ({
  title = 'Menu',
  text = null,
  icon = <MoreVertIcon sx={{ width: 24, height: 24 }} />,
  children,
  variant = BUTTON_VARIANTS.TEXT_PRIMARY,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
      }}
      >
        <Tooltip title={title}>
          <Button
            onClick={handleClick}
            aria-controls={open ? 'inner-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            variant={variant}
            icon={icon}
            data-testid={`menu-button-${title}`}
          >
            {text}
          </Button>
        </Tooltip>
      </Box>
      <StyledMenu
        anchorEl={anchorEl}
        id="inner-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgb(0 0 0 / 20%))',
            marginTop: 1.5,
            marginLeft: -2.5,
            '& .MuiAvatar-root': {
              width: 24,
              height: 24,
              marginLeft: -0.5,
              marginRight: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{ boxShadow: '0 4px 8px 0 rgb(0 0 0 / 20%), 0 6px 20px 0 rgb(0 0 0 / 20%)' }}
      >
        {children}
      </StyledMenu>
    </>
  );
};

Menu.propTypes = {
  text: PropTypes.node,
  icon: PropTypes.node,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  title: PropTypes.string,
  variant: PropTypes.string,
};
